/* eslint-disable max-lines */

import React, { Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'
import AvatarEditor from 'react-avatar-editor'

import { Button } from '@wf-common/atoms'

import { checkImageMimeType } from '../../util/image-processing'
import FocusAreaBlind from '../focus-area/focus-area-blind'
import Dialog from '../modal/dialog'
import { Slider } from '../slider/slider'

import styles from './image-editor.module.css'

const labelsProp = PropTypes.shape({
    apply: PropTypes.string,
    button: PropTypes.node,
    cancel: PropTypes.string,
    errorMessage: PropTypes.string,
    headline: PropTypes.string,
    legalNote: PropTypes.func,
    message: PropTypes.string,
    timeToggle: PropTypes.string
})

const propTypes = {
    applyButtonDisabled: PropTypes.bool,
    borderRadius: PropTypes.number,
    dialogClassName: PropTypes.string,
    disabled: PropTypes.bool,
    fileType: PropTypes.string,
    // Firefox will not support .svg that doesn't provide height and width
    fitImage: PropTypes.bool,

    height: PropTypes.number.isRequired,

    // This prevents that the image is getting cropped by imageEditor.
    id: PropTypes.number,

    initialScale: PropTypes.number,

    labels: labelsProp.isRequired,
    maxScale: PropTypes.number,
    offsetBorder: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),
    onChange: PropTypes.func.isRequired,
    scaleStep: PropTypes.number,
    sliderClassName: PropTypes.string,
    width: PropTypes.number.isRequired
}

const defaultProps = {
    // initial scaling of the image
    applyButtonDisabled: false,
    borderRadius: 0,
    fileType: '.png, .jpg',
    fitImage: false,
    initialScale: 1,
    maxScale: 3,

    // specify the semitransparent area around the image
    offsetBorder: 20,

    scaleStep: 0.01
}

/**
 * This component allows the user to select, crop and zoom a locally stored image.
 *
 * The component is represented as a simple button. When pressing the button a file picker is opened.
 * If the user selects an image, an image editing dialog is opened.
 *
 * If the image is edited, the user can press an 'apply' button, which returns the edited image
 * via the onChange(img) callback.
 *
 * For examples please refer to the showcase.
 */
class ImageEditor extends Component {
    constructor(props) {
        super(props)
        const { initialScale } = this.props

        this.state = {
            error: false,
            isOpen: false,
            position: {
                x: 0.5,
                y: 0.5
            },
            rotate: 0,
            scale: initialScale,
            showFocusArea: false
        }

        this.handleNewImage = this.handleNewImage.bind(this)
        this.handleScale = this.handleScale.bind(this)
        this.handlePreview = this.handlePreview.bind(this)
        this.onClose = this.onClose.bind(this)
        this.onError = this.onError.bind(this)
    }

    render() {
        const { showFocusArea } = this.state
        return (
            <div>
                <FocusAreaBlind isShown={showFocusArea} className={styles['focus-area']} onClick={() => {}} />
                {this.renderButton()}
                {this.renderDialog()}
                {this.renderErrorDialog()}
            </div>
        )
    }

    onClickInput(event) {
        // if the user selects the same picture twice, there is no onChange() fired, so set value to null
        event.target.value = null
    }

    renderButton() {
        const { fileType, id, labels } = this.props
        // This function needs to be adjusted to use ButtonGroup when using ImageEditor in DetailViews.

        const inputButton = (
            <div>
                <label htmlFor={'t3sel-image-editor' + id} className={styles['upload-Label']}>
                    {labels.button}
                </label>
                <input
                    className={styles['input-hide']}
                    id={'t3sel-image-editor' + id} // in case of more than one imageEditors the id needs to be unique
                    name="newImage"
                    type="file"
                    accept={fileType}
                    onChange={this.handleNewImage}
                    onClick={this.onClickInput}
                />
            </div>
        )

        return inputButton
    }

    renderDialog() {
        const {
            applyButtonDisabled,
            borderRadius,
            children,
            dialogClassName,
            height,
            labels,
            maxScale,
            offsetBorder,
            scaleStep,
            sliderClassName,
            width
        } = this.props
        const { image, isOpen, rotate, scale } = this.state

        const scaleControl = image ? (
            <div className={cx(styles['slider-container'], sliderClassName)}>
                <Slider min={1} value={scale} max={maxScale} onChange={this.handleScale} step={scaleStep} />
            </div>
        ) : null

        return (
            <Dialog
                id="fileUploadDialog"
                title={labels.headline}
                message={labels.message}
                isOpen={isOpen}
                className={cx('t3sel-image-editor-dialog', dialogClassName)}
            >
                <div className={styles['avatar-editor-container']}>
                    <AvatarEditor
                        id="fileUploadAvatarEditor"
                        ref={editor => (this.editor = editor)}
                        image={image || this.placeholder}
                        width={width}
                        height={height}
                        border={offsetBorder}
                        rotate={rotate}
                        scale={scale}
                        color={[255, 255, 255, 0.8]}
                        borderRadius={borderRadius}
                    />
                    {scaleControl}
                </div>
                {children}
                <nav className={styles['buttons']}>
                    <Button className={cx(styles['button'], 't3sel-cancel-button')} onClick={this.onClose}>
                        {labels.cancel}
                    </Button>
                    <Button
                        className={cx(styles['button'], 't3sel-apply-button')}
                        disabled={!!applyButtonDisabled}
                        primary
                        onClick={this.handlePreview}
                    >
                        {labels.apply}
                    </Button>
                </nav>
            </Dialog>
        )
    }

    renderErrorDialog() {
        const {
            labels: { cancel, errorMessage }
        } = this.props
        const { error } = this.state
        return (
            <Dialog id="fileUploadErrorDialog" title="" message={errorMessage} isOpen={error && !!errorMessage}>
                <nav className={styles['buttons']}>
                    <Button className={styles['button']} onClick={this.onClose}>
                        {cancel}
                    </Button>
                </nav>
            </Dialog>
        )
    }

    onClose() {
        this.setState({
            error: false,
            fileData: null,
            image: null,
            isOpen: false,
            showFocusArea: false
        })
    }

    handleNewImage(e) {
        const { fileType, fitImage } = this.props
        const { initialScale } = this.state
        const imageFile = e.target.files[0]

        if (imageFile instanceof File && checkImageMimeType(imageFile, fileType)) {
            // Note: react-avatar-editor v10 supports File object, but it's based on React 15.5,
            // which is not recommended for atlantis
            // Therefore use react-avatar-editor v8, which is based on React 15.3, but doesn't support File object
            // --> read file and pass an URI to react-avatar-editor instead
            const reader = new FileReader()
            reader.onload = e => {
                // When in landscape mode picture needs to be adapted to fit in height or width
                if (fitImage) {
                    this.fitImage(e.target.result).then(img => {
                        this.setState({
                            fileData: imageFile,
                            image: img,
                            isOpen: true,
                            scale: initialScale,
                            showFocusArea: true
                        })
                    })
                } else {
                    this.setState({
                        fileData: imageFile,
                        image: e.target.result,
                        isOpen: true,
                        scale: initialScale,
                        showFocusArea: true
                    })
                }
            }
            reader.readAsDataURL(imageFile)
        } else {
            this.onError()
        }
    }

    onError() {
        const { onChange } = this.props
        this.setState({
            error: true,
            fileData: null,
            image: null,
            isOpen: false,
            showFocusArea: false
        })
        onChange(null, null)
    }

    handleScale(value) {
        const scale = parseFloat(value)
        this.setState({ scale })
    }

    handlePreview(data) {
        const { editor } = this
        const { onChange } = this.props
        const { fileData } = this.state
        const img = editor.getImageScaledToCanvas().toDataURL()
        onChange(img, fileData)
        this.onClose()
    }

    /**
     * Helper to fill image for rectangular landscape editors with bg color (fixed to white for reports)
     *
     * @param {string} imgDataUri
     * @returns {Promise}
     */
    fitImage(imgDataUri) {
        const { height, maxScale, width } = this.props
        const canvasHeight = height * maxScale // The size of the canvas is depending on maximal zoomLevel (maxScale).
        const canvasWidth = width * maxScale // The bigger the maxScale, the longer it'll take to create this canvas.

        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas')
            if (!canvas) {
                return reject('no canvas created')
            }

            const img = new Image()

            img.onload = () => {
                // avoid memory leaks
                img.onload = img.onerror = null

                const imgRatio = img.width / img.height
                const canvasRatio = width / height

                const drawOnCanvas = ({ canvasHeight, canvasWidth, dHeight, dWidth, dx, dy, image }) => {
                    const cContext = canvas.getContext('2d')
                    // set fillstyle to 'white'
                    cContext.fillStyle = 'white'
                    // fill Background white
                    cContext.fillRect(0, 0, canvasWidth, canvasHeight)
                    cContext.drawImage(image, dx, dy, dWidth, dHeight)
                    return canvas.toDataURL()
                }

                // If imageRatio is smaller than destinationSize add white space to width.
                if (imgRatio < canvasRatio) {
                    // Scale the image to the size of canvas.
                    const scaledImageHeight = canvasHeight
                    const scaledImageWidth = (canvasHeight / img.height) * img.width

                    // set canvas dimensions to according to the image height and the
                    // precalculated width according to ratio and add space for moving image to left and right.
                    const scaledCanvasWidth = canvasWidth * 2 - scaledImageWidth
                    canvas.setAttribute('height', canvasHeight)
                    canvas.setAttribute('width', scaledCanvasWidth)

                    // dx is the coordinate in the destination canvas at which to place the top-left
                    // corner of the source image.
                    const dx = canvasWidth - scaledImageWidth
                    const dy = 0

                    resolve(
                        drawOnCanvas({
                            canvasHeight: canvasHeight,
                            canvasWidth: scaledCanvasWidth,
                            dHeight: scaledImageHeight,
                            dWidth: scaledImageWidth,
                            dx: dx,
                            dy: dy,
                            image: img
                        })
                    )
                }

                // If imageRatio is same or bigger than destinationSize add white space to height.
                else {
                    const scaledImageHeight = (canvasWidth / img.width) * img.height
                    const scaledImageWidth = canvasWidth
                    const scaledCanvasHeight = canvasHeight * 2 - scaledImageHeight
                    canvas.setAttribute('height', scaledCanvasHeight)
                    canvas.setAttribute('width', scaledImageWidth)
                    const dx = 0
                    const dy = canvasHeight - scaledImageHeight

                    resolve(
                        drawOnCanvas({
                            canvasHeight: scaledCanvasHeight,
                            canvasWidth: canvasWidth,
                            dHeight: scaledImageHeight,
                            dWidth: scaledImageWidth,
                            dx: dx,
                            dy: dy,
                            image: img
                        })
                    )
                }
            }

            img.onerror = e => {
                // avoid memory leaks
                img.onload = img.onerror = null
                reject(e)
            }

            img.src = imgDataUri

            // check if image is cached, trigger load manually
            if (!!img.complete && img.naturalWidth !== undefined) {
                img.onload()
            }
        })
    }
}

ImageEditor.propTypes = propTypes
ImageEditor.defaultProps = defaultProps

export default ImageEditor
